/**
 * Your Gmail: connect it so email with people in the hub is captured on their
 * timeline (subject and a preview of the message), sync now, or disconnect.
 */
import { AlertTriangle, Mail, RefreshCw, Unplug } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { del, post } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { ago, fmtDate, fmtDateTime, int } from "../../lib/format";
import { useMailStatus, useWrite } from "../../lib/queries";
import { ConfirmDialog, Dialog, Tooltip } from "../../components/overlay";
import { Button, DetailRow, Loading, Notice } from "../../components/ui";

export function GmailDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const cfg = useConfig();
  const status = useMailStatus(open);
  const [confirmOff, setConfirmOff] = useState(false);
  const conn = status.data?.connection ?? null;
  const s = cfg.bundle.settings;

  const connect = useWrite(() => post<{ url: string | null }>("/api/mail/connect"), {
    onSuccess: (r) => {
      if (r.url) window.location.assign(r.url);
      else toast.success("Gmail connected");
    },
  });
  // Runs in the background; the dialog follows its progress.
  const sync = useWrite(() => post("/api/mail/sync"));
  const off = useWrite(() => del("/api/mail"), { success: "Gmail disconnected", onSuccess: () => setConfirmOff(false) });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Gmail"
      description={conn?.email}
      footer={
        conn ? (
          <>
            <Button variant="ghost" icon={<Unplug className="size-4" />} onClick={() => setConfirmOff(true)}>
              Disconnect
            </Button>
            <Button
              variant="primary"
              icon={<RefreshCw className="size-4" />}
              loading={sync.isPending || conn.syncing}
              disabled={conn.backfilling || conn.syncing || conn.status !== "connected"}
              onClick={() => sync.mutate(undefined)}
            >
              Sync now
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" icon={<Mail className="size-4" />} loading={connect.isPending} disabled={status.data?.mode === "off"} onClick={() => connect.mutate(undefined)}>
              Connect Gmail
            </Button>
          </>
        )
      }
    >
      {status.isLoading ? (
        <Loading />
      ) : status.data?.mode === "off" ? (
        <Notice tone="warn">Gmail capture is switched off on this hub.</Notice>
      ) : !conn ? (
        <ul className="space-y-1.5 text-sm text-body">
          <li>Email to and from people in the hub is added to their timeline: subject, and up to {int(s.mailPreviewChars)} characters of the message.</li>
          <li>Starts with the last {int(s.mailBackfillDays)} days, then new email every few minutes.</li>
          <li>Email labelled “{s.mailSkipLabel}” in Gmail is never captured.</li>
          <li>Read-only: the hub never sends, changes or deletes anything in your mailbox.</li>
        </ul>
      ) : (
        <div className="space-y-4">
          {conn.status === "error" && (
            <Notice
              tone="danger"
              icon={<AlertTriangle className="size-4" />}
              title="Gmail needs reconnecting"
              action={
                <Button size="sm" loading={connect.isPending} onClick={() => connect.mutate(undefined)}>
                  Reconnect
                </Button>
              }
            >
              {conn.lastError}
            </Notice>
          )}
          {conn.status === "connected" && conn.lastError && <Notice tone="warn">{conn.lastError}</Notice>}
          {conn.backfilling && conn.status === "connected" && (
            <Notice tone="info">
              Reading the last {int(s.mailBackfillDays)} days{conn.readUpTo ? `: up to ${fmtDate(conn.readUpTo)} so far` : "…"}
            </Notice>
          )}
          <dl>
            <DetailRow label="Emails captured">
              <span className="num">{int(conn.captured)}</span>
            </DetailRow>
            <DetailRow label="Last checked">
              {conn.lastSyncedAt ? (
                <Tooltip content={fmtDateTime(conn.lastSyncedAt)}>
                  <span tabIndex={0}>{ago(conn.lastSyncedAt)}</span>
                </Tooltip>
              ) : (
                "Not yet"
              )}
            </DetailRow>
            <DetailRow label="Connected">{fmtDateTime(conn.connectedAt)}</DetailRow>
            <DetailRow label="Not captured">Email labelled “{s.mailSkipLabel}”</DetailRow>
          </dl>
        </div>
      )}
      <ConfirmDialog open={confirmOff} onOpenChange={setConfirmOff} title="Disconnect Gmail?" confirmLabel="Disconnect" loading={off.isPending} onConfirm={() => off.mutate(undefined)}>
        Capture stops. Email already captured stays on the timeline.
      </ConfirmDialog>
    </Dialog>
  );
}
